/**
 * Combined drafting-aid constraint resolver — applies the ORTHO (F8) and POLAR
 * (F10) locks to a resolved draft cursor in the single order AutoCAD uses.
 *
 * Both aids project the emit onto the same draft anchor ($LASTPOINT). They are
 * MUTUALLY EXCLUSIVE in AutoCAD (enabling one disables the other); this resolver
 * applies ortho first, then polar (whose gate internally stands down when ortho
 * is effective), so the cursor is never double-constrained. OSNAP magnet wins
 * over both — the individual gates ({@link orthoLockedCursor} /
 * {@link polarLockedCursor}) each pass through a magnetised emit and the
 * anchor-less first point.
 *
 * Extracted from the pointer-input seam (`stageInteractions`) so that file stays
 * under its 2000-line cap and the aid-precedence lives in one THREE-free,
 * unit-testable place — the same split pattern as `stageCameraMath`.
 */

import { orthoLockedCursor } from "./orthoDraftLock";
import { polarLockedCursor } from "./polarDraftLock";
import { otrackLockedCursor } from "./otrackDraftLock";
import {
  clipSegmentToRect,
  viewportPaperRect,
  type ViewportLike,
  type PaperOrigin,
} from "../viewports/viewportTransform2d";

type Point2D = { x: number; y: number };

export interface DraftAidResult {
  /** ORTHO- or POLAR-locked emit coord (whichever engaged; else raw). */
  readonly world: Point2D;
  /**
   * POLAR tracking guide-line endpoints, in OVERLAY/PAPER coords (already mapped
   * through `guideToOverlay`), or null when idle OR when TRACKPATH suppresses
   * the line (`pathVisible: false`) OR the guide clips entirely off-frame.
   * `OverlayState.polarGuide` is drawn by the paper-camera world overlay, so
   * inside a viewport the guide must be reprojected from the model-space lock
   * back to paper (#492 @codex P2).
   *
   * NOT an engagement signal — see {@link polarEngaged} (local Codex review
   * round 2 P1: a caller, `aidedPointerSnap.ts`, inferred "ortho engaged, not
   * polar" from `polarGuide == null`, which broke once TRACKPATH could null
   * this field while polar was genuinely engaged).
   */
  readonly polarGuide: {
    from: Point2D;
    to: Point2D;
  } | null;
  /** POLAR "distance < angle°" tooltip text, or null when idle/off-frame. */
  readonly polarTip: string | null;
  /**
   * Whether POLAR geometrically ENGAGED (a ray locked `world`) — TRUE even
   * when `polarGuide`/`polarTip` end up null (TRACKPATH-suppressed line, or
   * an off-frame clip). The sole correct "polar vs ortho" discriminator for
   * callers that need to know WHICH aid moved `world`; `polarGuide == null`
   * is NOT reliable for that once TRACKPATH/off-frame-clip can null it while
   * still engaged (local Codex review round 2 P1).
   */
  readonly polarEngaged: boolean;
  /**
   * OTRACK-SUBSYSTEM — engaged alignment-path guide lines, in OVERLAY/PAPER
   * coords, one entry per engaged ray (1 = single acquired-point path, 2 =
   * combined tracking from two acquired points). Empty when OTRACK did not
   * engage. Unlike `polarGuide`, this is an ARRAY (CLAUDE.md ПРИОРИТЕТ #1 —
   * AutoCAD genuinely supports simultaneous multi-path tracking, so a single
   * from/to pair would silently drop the second line).
   */
  readonly otrackGuides: ReadonlyArray<{ from: Point2D; to: Point2D }>;
  /** OTRACK tracking tooltip (one "distance < angle°" reading per engaged ray), or null when idle. */
  readonly otrackTip: string | null;
  /** True IFF OTRACK geometrically engaged — the sole "did OTRACK lock" signal (mirrors `polarEngaged`). */
  readonly otrackEngaged: boolean;
}

/**
 * Resolve the active drawing-aid constraint for `emitWorld` against the draft
 * `anchor`. Feed the PRE-ortho emit: when polar actually engages ortho is
 * inactive, so `orthoWorld === emitWorld` and the polar result wins; otherwise
 * the ortho result (which is `emitWorld` when ortho is also off) stands.
 *
 * The aid runs entirely in the EMIT (model) space. `world` is returned in that
 * emit space (it feeds the commit tagged with the emit space id), but the polar
 * guide endpoints are projected through `guideToOverlay` (the active input
 * space's `fromSpace`, modelToPaper inside a VP / identity outside) so the dashed
 * overlay line lands inside the viewport rather than at raw model coords on the
 * sheet (#492 @codex P2 — same reason `pointerSnapStep` maps `snapTarget.world`).
 *
 * When `viewport` + `paperOrigin` are supplied (an active VP redirect), the
 * paper-space guide is additionally CLIPPED to that VP's frame rect so the dashed
 * overlay never leaks across the layout sheet outside the viewport as the draft is
 * dragged past the edge — the draft rubber band is trimmed by render-side clip
 * planes, and this paper-camera overlay needs the same trim (#492 @codex P2). A
 * guide clipped entirely off-frame hides both the line and the tip (nothing on
 * screen to anchor either to).
 *
 * `world` uses `polar.world` whenever `polar.guide` is non-null (i.e. whenever
 * polar geometrically ENGAGED), UNCONDITIONALLY — never gated on whether the
 * line/tip end up displayed. TRACKPATH (`polar.pathVisible`) governs ONLY
 * whether `polarGuide` (the dashed alignment-path LINE) is populated; `polarTip`
 * stays populated whenever engaged + on-frame, matching AutoCAD (TRACKPATH is
 * documented as gating the alignment path only, never the AutoTrack tooltip —
 * local Codex review round 1 P1/P2: an earlier revision used `polar.guide` as
 * BOTH the engagement signal AND the display gate, so TRACKPATH suppressing the
 * line silently discarded the LOCKED point too and wrongly hid the tooltip).
 */
export function resolveDraftAidConstraint(
  emitWorld: Point2D,
  anchor: Point2D | null,
  snapMagnetized: boolean,
  guideToOverlay: (pt: Point2D) => Point2D,
  viewport?: ViewportLike | null,
  paperOrigin?: PaperOrigin | null,
  /**
   * N6 (POLAR-RELATIVE-BASIS-WIRE) — direction (degrees) of the last segment
   * drawn, for POLARMODE's relative-basis table rotation. `null`/omitted when
   * there is none (first segment) — forwarded verbatim to
   * {@link polarLockedCursor}, which itself only consults it under relative
   * basis. Never affects ORTHO.
   */
  referenceAngleDeg?: number | null,
  /**
   * OTRACK-SUBSYSTEM — OSNAP aperture in WORLD units (mirrors how
   * `resolveObjectSnap` sizes its own aperture: `aperturePx /
   * pixelsPerWorld`). `0`/omitted disables OTRACK engagement for this call
   * (every pre-OTRACK call site stays byte-identical).
   */
  otrackApertureWorld = 0,
  /**
   * OTRACK-SUBSYSTEM (local Codex review round 5 P2) — forwarded verbatim to
   * {@link otrackLockedCursor}'s `currentSpaceId` — see its doc.
   * `null`/omitted disables the acquired-point space filter (every
   * pre-existing call site stays byte-identical); the production caller
   * (`aidedPointerSnap.ts`) always supplies its own `emitSpaceId`.
   */
  emitSpaceId: string | null = null,
  /**
   * OTRACK-SUBSYSTEM — forwarded verbatim to {@link otrackLockedCursor}'s
   * `fromDocument`: the DOCUMENT → ACTIVE-SPACE conversion for the acquired-
   * point store (see its doc). `null`/omitted keeps every pre-existing call
   * site byte-identical; the production caller (`aidedPointerSnap.ts`) passes
   * the emit space's own `ActiveInputSpace.fromDocument`.
   */
  otrackFromDocument: ((p: Point2D) => Point2D) | null = null,
): DraftAidResult {
  // OTRACK is checked FIRST and, when it engages, takes precedence over
  // ORTHO/POLAR (the most specific constraint — an explicit acquired point —
  // wins; see the design note's "precedence decision"). Unlike ortho/polar it
  // needs NO anchor: AutoCAD lets OTRACK engage on a command's first point.
  const otrack = otrackLockedCursor(
    emitWorld,
    snapMagnetized,
    otrackApertureWorld,
    referenceAngleDeg,
    emitSpaceId,
    otrackFromDocument,
  );
  if (otrack.guide) {
    const guides = otrack.guide.rays.map((ray) => {
      const from = guideToOverlay(ray.acquiredWorld);
      const to = guideToOverlay(ray.point);
      const trimmed =
        viewport && paperOrigin
          ? clipSegmentToRect(from, to, viewportPaperRect(viewport, paperOrigin))
          : { from, to };
      return trimmed;
    });
    const visibleGuides = guides.filter(
      (g): g is { from: Point2D; to: Point2D } => g !== null,
    );
    return {
      world: otrack.world,
      polarGuide: null,
      polarTip: null,
      polarEngaged: false,
      otrackGuides: visibleGuides,
      otrackTip: otrack.guide.tip,
      otrackEngaged: true,
    };
  }

  const orthoWorld = orthoLockedCursor(emitWorld, anchor, snapMagnetized);
  const polar = polarLockedCursor(
    emitWorld,
    anchor,
    snapMagnetized,
    referenceAngleDeg,
  );
  if (polar.guide) {
    const from = guideToOverlay(polar.guide.from);
    const to = guideToOverlay(polar.guide.to);
    const trimmed =
      viewport && paperOrigin
        ? clipSegmentToRect(from, to, viewportPaperRect(viewport, paperOrigin))
        : { from, to };
    // Off-frame clip hides BOTH (nothing to anchor to) — checked before the
    // TRACKPATH gate so an off-frame guide is never partially surfaced.
    // `polarEngaged` stays true: polar DID lock `world`, only the display
    // clipped away (same discriminator rationale as the TRACKPATH branch).
    if (!trimmed) {
      return {
        world: polar.world,
        polarGuide: null,
        polarTip: null,
        polarEngaged: true,
        otrackGuides: [],
        otrackTip: null,
        otrackEngaged: false,
      };
    }
    // TRACKPATH (`pathVisible`) gates the LINE only; `world` and the tooltip
    // are unconditional once `polar.guide` is non-null (engaged).
    return {
      world: polar.world,
      polarGuide: polar.pathVisible ? trimmed : null,
      polarTip: polar.guide.tip,
      polarEngaged: true,
      otrackGuides: [],
      otrackTip: null,
      otrackEngaged: false,
    };
  }
  return {
    world: orthoWorld,
    polarGuide: null,
    polarTip: null,
    polarEngaged: false,
    otrackGuides: [],
    otrackTip: null,
    otrackEngaged: false,
  };
}
